import { useMemo } from 'react'
import { useDebounce } from '@/hooks/use-debounce'
import { extractApiErrorMessage, extractApiMessage } from '@/lib/api-message'
import { isUsernameFormatValid } from '@/lib/auth-validation'
import { useCheckUsernameQuery } from '@/store/api/authApi'

const USERNAME_CHECK_DEBOUNCE_MS = 400
const USERNAME_AVAILABLE_MESSAGE = 'Username is available'
const USERNAME_TAKEN_MESSAGE = 'Username is already taken'

export type UsernameAvailabilityTone = 'success' | 'destructive'

interface UsernameAvailabilityState {
	message?: string
	tone?: UsernameAvailabilityTone
	isChecking: boolean
	isAvailable?: boolean
	isChecked: boolean
}

export function useUsernameAvailability(username: string): UsernameAvailabilityState {
	const trimmedUsername = username.trim()
	const debouncedUsername = useDebounce(trimmedUsername, USERNAME_CHECK_DEBOUNCE_MS)
	const isDebouncing = trimmedUsername !== debouncedUsername
	const canCheck = isUsernameFormatValid(debouncedUsername)
	const canCheckCurrent = isUsernameFormatValid(trimmedUsername)

	const { data, error, isFetching, isError, isUninitialized } = useCheckUsernameQuery(debouncedUsername, {
		skip: !canCheck,
	})

	return useMemo(() => {
		const isChecking = canCheckCurrent && (isDebouncing || (canCheck && isFetching))
		const hasSettledResult = canCheck && !isDebouncing && !isFetching && !isUninitialized

		if (!canCheckCurrent) {
			return {
				isChecking: false,
				isChecked: false,
			}
		}

		if (isChecking) {
			return {
				isChecking: true,
				isChecked: false,
			}
		}

		if (!hasSettledResult) {
			return {
				isChecking: false,
				isChecked: false,
			}
		}

		if (isError) {
			return {
				message: 'Something went wrong. Please try again.',
				tone: 'destructive',
				isChecking: false,
				isAvailable: false,
				isChecked: true,
			}
		}

		if (data) {
			const isAvailable = data.data.available

			return {
				message: isAvailable ? USERNAME_AVAILABLE_MESSAGE : USERNAME_TAKEN_MESSAGE,
				tone: isAvailable ? 'success' : 'destructive',
				isChecking: false,
				isAvailable,
				isChecked: true,
			}
		}

		return {
			isChecking: false,
			isChecked: false,
		}
	}, [canCheck, canCheckCurrent, data, error, isDebouncing, isError, isFetching, isUninitialized])
}
